import type { MarketLike } from "@/hooks/useRealtimeMarketPrice";

export interface Market {
  id: string;
  question: string;
  groupItemTitle?: string | null;
  slug: string;
  conditionId: string;
  outcomes: string[];
  outcomePrices: number[];
  clobTokenIds: string[];
  lastTradePrice?: number;
  bestBid?: number;
  bestAsk?: number;
  acceptingOrders: boolean;
  active: boolean;
  closed: boolean;
  volume: number;
  /** 24h rolling volume in USD. Polymarket exposes this natively
   * (`volume24hr`); other sources fall back to undefined. */
  volume24hr?: number;
  liquidity: number;
  openInterest?: number;
  enableOrderBook: boolean;
  image?: string | null;
  imageUrl?: string | null;
  endDate?: string | null;
  oneDayPriceChange?: number | null;
  oneHourPriceChange?: number | null;
  rewardsMaxSpread?: number;
  rewardsMinSize?: number;
  rewardsRatePerDay?: number;
  /** Predict.fun orderbook decimal precision (0=1¢ ticks, 1=0.1¢, 2=0.01¢) */
  decimalPrecision?: number;
}

export type Source = 'polymarket' | 'kalshi' | 'predict' | 'hyperliquid';

export type OpenTradingModalFn = (params: {
  isOpen: boolean;
  marketSlug: string;
  outcome: "YES" | "NO";
  outcomePrice: number;
  marketQuestion: string;
  actualOutcome?: string;
  outcomes?: string[];
} | null) => void;

export type GetLivePriceFn = (market: MarketLike) => [number, number];

export type GetLiveBuyPricesFn = (market: MarketLike) => {
  buyYes: number;
  buyNo: number;
  isRealOrderbook: boolean;
  hasRealBuyYes?: boolean;
  hasRealBuyNo?: boolean;
};

export type FormatVolumeFn = (vol: number | string | null | undefined) => string;

/**
 * Format price in cents respecting decimal precision.
 * - Predict.fun: uses explicit decimalPrecision field (0, 1, 2)
 * - Kalshi: subpenny pricing (72.5¢) — auto-detect from value
 * - Polymarket: always integer cents
 */
export function formatPriceCents(price: number, decimalPrecision?: number): string {
  const cents = (price ?? 0) * 100;
  // Explicit precision (Predict.fun)
  if (decimalPrecision != null && decimalPrecision > 0) {
    return cents.toFixed(decimalPrecision);
  }
  // Auto-detect subpenny (Kalshi) — show 1 decimal if value has fractional cents
  const rounded = Math.round(cents);
  if (Math.abs(cents - rounded) > 0.05) {
    return cents.toFixed(1);
  }
  return rounded.toString();
}

/**
 * Parse team abbreviations from a sports event slug.
 * e.g. "cbb-uconn-ill-2026-04-04" → { home: "UCONN", away: "ILL" }
 * Returns null for non-sports slugs.
 */
export function parseSlugTeamAbbrs(slug: string): { home: string; away: string } | null {
  const parts = slug.split('-');
  if (parts.length < 5) return null;
  const dateParts = parts.slice(-3);
  if (!/^\d{4}$/.test(dateParts[0])) return null;
  const teamParts = parts.slice(1, -3); // skip league prefix
  if (teamParts.length < 2) return null;
  return {
    home: teamParts[0].toUpperCase(),
    away: teamParts.slice(1).join('-').toUpperCase(),
  };
}

/**
 * Get display label for an outcome, using slug abbreviation when available.
 * Maps outcome to home/away abbreviation by matching against the event title.
 */
export function getOutcomeAbbr(outcome: string, eventSlug: string, eventTitle?: string): string {
  const abbrs = parseSlugTeamAbbrs(eventSlug);
  if (!abbrs || !eventTitle) return outcome;
  // Match outcome to home/away by checking which part of the title it appears in
  // Title format: "Team A vs. Team B" or "Team A vs Team B"
  const titleParts = eventTitle.split(/\s+vs\.?\s+/i);
  if (titleParts.length !== 2) return outcome;
  const outcomeLower = outcome.toLowerCase();
  // Direct match: outcome IS one of the teams in the title
  if (titleParts[0].toLowerCase().includes(outcomeLower) || outcomeLower.includes(titleParts[0].toLowerCase().trim())) {
    return abbrs.home;
  }
  if (titleParts[1].toLowerCase().includes(outcomeLower) || outcomeLower.includes(titleParts[1].toLowerCase().trim())) {
    return abbrs.away;
  }
  return outcome;
}
